import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import {
  Target, Sparkle, ArrowsClockwise, Star, Link as LinkIcon, Copy,
  WarningCircle, CheckCircle, Trophy, Info,
} from "@phosphor-icons/react";

export default function CoacheeCompetencePage() {
  const [coachee, setCoachee] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingScore, setSavingScore] = useState(false);
  const [error, setError] = useState("");
  const [copyToast, setCopyToast] = useState(false);

  useAITaskRegistration({
    active: generating && !!coachee,
    id: coachee ? `competence-${coachee.id}` : null,
    label: "20 competenties genereren met AI",
    returnTo: "/tests/competenties",
  });

  const bootstrap = async () => {
    try {
      const { data: u } = await api.get("/auth/me");
      const { data: list } = await api.get("/coachees");
      const own = list.find((p) => p.user_id === u.id) || null;
      setCoachee(own);
      if (own) {
        const { data } = await api.get(`/coachees/${own.id}/competence`);
        setSession(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { bootstrap(); }, []);

  const handleGenerate = async () => {
    if (!coachee) return;
    setError("");
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/coachees/${coachee.id}/competence/generate`,
        null,
        { timeout: 180000 },
      );
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const updateScore = async (compName, value) => {
    if (!coachee) return;
    // optimistic
    setSession((prev) => ({
      ...prev,
      self_scores: { ...(prev?.self_scores || {}), [compName]: value },
    }));
    setSavingScore(true);
    try {
      const { data } = await api.put(`/coachees/${coachee.id}/competence/self-score`, {
        scores: { [compName]: value },
      });
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingScore(false);
    }
  };

  const feedbackUrl = useMemo(() => {
    if (!session?.feedback_token || typeof window === "undefined") return "";
    return `${window.location.origin}/feedback/competentie/${session.feedback_token}`;
  }, [session?.feedback_token]);

  const copyLink = async () => {
    if (!feedbackUrl) return;
    try {
      await navigator.clipboard.writeText(feedbackUrl);
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2000);
    } catch {
      window.prompt("Kopieer deze link:", feedbackUrl);
    }
  };

  const competencies = session?.competencies || [];
  const hasCompetencies = competencies.length > 0;
  const allScored = hasCompetencies && competencies.every((c) => (session?.self_scores || {})[c.name]);
  const regenLeft = Math.max(
    0,
    (session?.coachee_regen_limit || 1) - (session?.coachee_regen_count || 0),
  );
  const canRegen = !!session?.coachee_can_regenerate && !generating;

  return (
    <AppLayout title="Competentietest">
      <div className="max-w-4xl space-y-6" data-testid="competence-page">
        {loading ? (
          <div className="text-zinc-500">Laden...</div>
        ) : !coachee ? (
          <div className="text-zinc-500">Coachee-profiel niet gevonden.</div>
        ) : (
          <>
            <IntroCard
              hasCompetencies={hasCompetencies}
              generating={generating}
              session={session}
              canRegen={canRegen}
              regenLeft={regenLeft}
              onGenerate={handleGenerate}
            />

            {error && (
              <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="competence-error">
                <WarningCircle size={18} className="shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            )}

            {hasCompetencies && (
              <>
                <FeedbackShareCard
                  url={feedbackUrl}
                  count={session.feedback_count}
                  onCopy={copyLink}
                  showToast={copyToast}
                />
                <CompetencyList
                  competencies={competencies}
                  selfScores={session.self_scores || {}}
                  feedbackAvg={session.feedback_avg || {}}
                  onScore={updateScore}
                  savingScore={savingScore}
                />
                {allScored && session.underutilised_top3?.length > 0 && (
                  <UnderutilisedCard items={session.underutilised_top3} />
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

function IntroCard({ hasCompetencies, generating, session, canRegen, regenLeft, onGenerate }) {
  if (!hasCompetencies) {
    return (
      <div className="neuro-card-bold p-6 bg-white" data-testid="competence-intro-empty">
        <div className="flex items-start gap-3">
          <Target size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-xl font-bold tracking-tight">Competentietest</h2>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
              De AI stelt op basis van je <strong>profielen</strong>, je <strong>CV</strong> en de talenten
              van je <strong>breintype</strong> een lijst van 20 competenties op die relevant zijn voor het
              vinden van een nieuwe baan. Daarna scoor je jezelf op een schaal van 1 tot 5 sterren, en
              kun je een link delen voor 360°-feedback van anderen.
            </p>
            <div className="mt-3 text-xs text-zinc-600 bg-zinc-50 border-l-2 border-[#1E88E5] px-3 py-2.5 leading-relaxed" data-testid="competence-intro-explainer">
              <strong className="text-zinc-900">Talent versus competentie.</strong> Een <em>talent</em> is
              een aangeboren biologisch voordeel van je breintype: gedrag dat bouwt op een talent leer
              je sneller en makkelijker en de kans dat je erin excelleert is groter. Een{" "}
              <em>competentie</em> is concreet, observeerbaar werkgedrag (een werkwoord) dat je kunt
              ontwikkelen. Competenties met een <span className="inline-block px-1 bg-[#FB8C00] text-white rounded-sm font-bold text-[10px]">✦</span>{" "}
              bouwen op één van je breintype-talenten.
            </div>
            <p className="text-xs text-amber-700 mt-3 leading-relaxed">
              Let op: je kunt deze lijst maar <strong>één keer zelf opnieuw laten genereren</strong> als
              je later veel nieuwe informatie toevoegt. Wil je daarna nóg wijzigingen?
              Vraag het je work coach.
            </p>
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              data-testid="competence-generate-btn"
              className="inline-flex items-center gap-2 mt-4 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] disabled:bg-zinc-400 transition-colors"
            >
              {generating ? <ArrowsClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
              {generating ? "Competenties genereren..." : "Genereer 20 competenties"}
            </button>
            {generating && (
              <p className="text-xs text-zinc-500 mt-2">
                Dit duurt circa 20-40 seconden. De AI leest je CV, profielen en breintype-talenten.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="neuro-card-bold p-5 bg-white" data-testid="competence-intro-filled">
      <div className="flex items-start gap-3 flex-wrap">
        <Target size={22} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-display text-lg font-bold tracking-tight">Jouw 20 competenties</h2>
          <p className="text-sm text-zinc-600 mt-1">
            Scoor jezelf op elke competentie. ✦ markeert competenties waarvoor jouw breintype een natuurlijk talent heeft.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canRegen ? (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              data-testid="competence-regen-btn"
              className="inline-flex items-center gap-2 bg-white border-2 border-zinc-900 text-zinc-900 px-3.5 py-2 text-xs font-semibold tracking-wide uppercase rounded-sm hover:bg-zinc-900 hover:text-white disabled:opacity-50 transition-colors"
            >
              {generating ? <ArrowsClockwise size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              Genereer opnieuw ({regenLeft} over)
            </button>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-200 px-3 py-2 rounded-sm bg-zinc-50 max-w-[280px] text-left leading-tight"
              data-testid="competence-regen-disabled"
            >
              <Info size={12} weight="duotone" />
              <span>
                {(session?.coachee_regen_count || 0) >= (session?.coachee_regen_limit || 1)
                  ? "Je hebt al opnieuw gegenereerd. Vraag je work coach voor een update."
                  : "Voeg eerst nieuwe info toe aan je CV, profielen of breintype om opnieuw te kunnen genereren."}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FeedbackShareCard({ url, count, onCopy, showToast }) {
  return (
    <div className="neuro-card p-5 bg-white" data-testid="competence-share-card">
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center bg-[#43A047]/10 text-[#43A047] rounded-sm shrink-0">
          <LinkIcon size={18} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold tracking-tight">360°-feedback link</h3>
          <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
            Deel deze link met collega's, vrienden of familie. Zij scoren jouw competenties op 1–5
            sterren — hun namen blijven geheim, jij ziet alleen het gemiddelde.
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <code
              className="flex-1 min-w-0 text-xs bg-zinc-100 border border-zinc-200 px-2.5 py-1.5 rounded-sm truncate"
              data-testid="competence-share-url"
              title={url}
            >
              {url}
            </code>
            <button
              type="button"
              onClick={onCopy}
              data-testid="competence-share-copy"
              className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-3 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
            >
              <Copy size={13} /> {showToast ? "Gekopieerd!" : "Kopieer"}
            </button>
          </div>
          <div className="mt-2 text-xs text-zinc-500" data-testid="competence-feedback-count">
            {count > 0
              ? `${count} ${count === 1 ? "persoon heeft" : "personen hebben"} feedback gegeven.`
              : "Nog niemand heeft feedback gegeven."}
          </div>
        </div>
      </div>
    </div>
  );
}

function StarRow({ value, onSelect, testidBase }) {
  return (
    <div className="flex items-center gap-1" data-testid={`${testidBase}-stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onSelect(n)}
          aria-label={`Score ${n}`}
          data-testid={`${testidBase}-star-${n}`}
          className="p-0.5 transition-transform hover:scale-110 outline-none focus:outline-none"
        >
          <Star
            size={20}
            weight={value && n <= value ? "fill" : "regular"}
            className={value && n <= value ? "text-[#FB8C00]" : "text-zinc-300"}
          />
        </button>
      ))}
    </div>
  );
}

function CompetencyList({ competencies, selfScores, feedbackAvg, onScore, savingScore }) {
  return (
    <div className="space-y-2" data-testid="competence-list">
      {competencies.map((c, i) => {
        const own = selfScores[c.name];
        const fb = feedbackAvg[c.name] || { avg: null, count: 0 };
        const isTalent = !!c.talent_match;
        return (
          <div
            key={c.name}
            className="neuro-card p-4 bg-white grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
            data-testid={`competence-row-${i}`}
          >
            <div className="md:col-span-6 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isTalent && (
                  <span
                    className="inline-flex items-center justify-center h-5 w-5 bg-[#FB8C00] text-white rounded-sm text-[11px] font-bold shrink-0"
                    title={
                      c.talents_tapped && c.talents_tapped.length > 0
                        ? `Bouwt op talent: ${c.talents_tapped.join(", ")}`
                        : "Bouwt op een talent van jouw breintype"
                    }
                    data-testid={`competence-talent-${i}`}
                  >✦</span>
                )}
                <h4 className="font-display text-base font-bold tracking-tight" data-testid={`competence-name-${i}`}>
                  {c.name}
                </h4>
              </div>
              {c.description && (
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{c.description}</p>
              )}
              {isTalent && c.talents_tapped && c.talents_tapped.length > 0 && (
                <p className="text-[10px] text-[#FB8C00] mt-1 uppercase tracking-widest font-semibold" data-testid={`competence-talents-tapped-${i}`}>
                  Talent: {c.talents_tapped.join(" · ")}
                </p>
              )}
            </div>
            <div className="md:col-span-3" data-testid={`competence-self-${i}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Eigen score</div>
              <StarRow value={own} onSelect={(n) => onScore(c.name, n)} testidBase={`competence-self-${i}`} />
            </div>
            <div className="md:col-span-3 text-right" data-testid={`competence-feedback-${i}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">360° gemiddeld</div>
              {fb.avg != null ? (
                <div className="flex items-center justify-end gap-1.5">
                  <Star size={16} weight="fill" className="text-[#FB8C00]" />
                  <span className="font-display text-sm font-bold">{fb.avg.toFixed(1)}</span>
                  <span className="text-xs text-zinc-500">/ 5</span>
                  <span className="text-[10px] text-zinc-400 ml-1">({fb.count})</span>
                </div>
              ) : (
                <span className="text-xs text-zinc-400">—</span>
              )}
            </div>
          </div>
        );
      })}
      {savingScore && (
        <div className="text-xs text-zinc-400 text-right" data-testid="competence-saving">
          Opslaan...
        </div>
      )}
    </div>
  );
}

function UnderutilisedCard({ items }) {
  return (
    <div className="neuro-card-bold p-6 bg-white" data-testid="competence-underutilised">
      <div className="flex items-start gap-3">
        <Trophy size={26} weight="duotone" className="text-[#FB8C00] shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Top 3 talenten die je nog onvoldoende benut
          </h3>
          <p className="text-sm text-zinc-600 mt-1">
            Op deze competenties heb je een natuurlijk talent op basis van je breintype, maar de gecombineerde
            score (eigen + 360°) wijst erop dat je dit talent nog niet ten volle gebruikt.
          </p>
          <ol className="mt-4 space-y-3">
            {items.map((it, i) => (
              <li key={it.name} className="flex items-start gap-3" data-testid={`competence-underu-${i}`}>
                <span className="inline-flex h-7 w-7 items-center justify-center bg-[#FB8C00] text-white rounded-sm font-bold text-sm shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <div className="font-semibold text-zinc-900">{it.name}</div>
                  {it.description && (
                    <div className="text-xs text-zinc-600 mt-0.5">{it.description}</div>
                  )}
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Eigen: {it.self_score ?? "—"} / 5{" · "}
                    360° gemiddeld: {it.feedback_avg != null ? it.feedback_avg.toFixed(1) : "—"} / 5
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
